import React, { useState } from "react";
import dayjs from "dayjs";
import { isBeforeToday, isWeekend, todayInSydney, toIsoDate } from "../../../utils/dayjs";
import { Header } from "../Header";
import { Panel } from "../Panel";
import { RentalDatesType } from "./RentalDateField";
import { RentalDatePickerDay } from "./RentalDatePickerDay";
import GcDayPicker, { DayModifiers } from "../GcDayPicker";
import "./rental-date-picker.scss";

type HighlightedDaysType = { from: Date; to: Date } | undefined;
interface RentalDatePickerProps {
  header?: string;
  isDressAvailable: (date: Date) => boolean;
  onDatePicked: (dates: RentalDatesType) => void;
  rentalPeriod: number;
  onClose?: () => void;
  availableDeliveryDates?: string[];
  highlightedDays?: HighlightedDaysType;
}

export const RentalDatePicker: React.FC<RentalDatePickerProps> = (props) => {
  const rentalPeriod = props.rentalPeriod;
  const [highlightedRentalDays, setHighlightedRentalDays] = useState<HighlightedDaysType>(props.highlightedDays);

  const isDateDisabled = (date: Date) => {
    if (!props.isDressAvailable(date)) {
      return true;
    }

    if (props.availableDeliveryDates && props.availableDeliveryDates.length > 0) {
      const isoDate = toIsoDate(date);
      const inDeliveryCalendar = props.availableDeliveryDates.indexOf(isoDate) > -1;
      return !inDeliveryCalendar;
    } else {
      return isBeforeToday(date) || isWeekend(date);
    }
  };

  const renderDay = (date: Date, modifiers: DayModifiers) => {
    return <RentalDatePickerDay date={date} modifiers={modifiers} />;
  };

  const isFirstHighlightedDay = (date: Date) => {
    if (!highlightedRentalDays) {
      return false;
    }
    return dayjs(date).isSame(highlightedRentalDays.from, "day");
  };

  const isLastHighlightedDay = (date: Date) => {
    if (!highlightedRentalDays) {
      return false;
    }
    return dayjs(date).isSame(highlightedRentalDays.to, "day");
  };

  const clearHighlight = () => {
    setHighlightedRentalDays(undefined);
  };

  const handleOnDayMouseEnter = (date: Date, modifier: DayModifiers) => {
    if (modifier.disabled) {
      clearHighlight();
    } else {
      setHighlightedRentalDays({
        from: date,
        to: dayjs(date)
          .add(rentalPeriod - 1, "day")
          .toDate(),
      });
    }
  };

  const handleOnDayClick = (startDate: Date, modifiers: DayModifiers) => {
    if (modifiers.disabled) {
      return;
    }

    const rentalDates = {
      startDate: toIsoDate(startDate),
      endDate: toIsoDate(
        dayjs(startDate)
          .add(rentalPeriod - 1, "day")
          .toDate()
      ),
    };

    props.onDatePicked(rentalDates);
    if (props.onClose) {
      props.onClose();
    }
  };

  return (
    <div className="js-rental-date-picker c-rental-date-picker__wrapper">
      <Panel ready={true} className="c-panel--with-header c-panel--no-border">
        <Header onClick={props.onClose} size="sm" as="h3">
          Pick your delivery date
        </Header>
        <p className="c-rental-date-picker__subheader">{props.header}</p>

        <GcDayPicker
          initialMonth={todayInSydney().toDate()}
          disabledDays={isDateDisabled}
          renderDay={renderDay}
          selectedDays={highlightedRentalDays}
          modifiers={{
            "selected-first": isFirstHighlightedDay,
            "selected-last": isLastHighlightedDay,
          }}
          onDayMouseEnter={handleOnDayMouseEnter}
          onDayClick={handleOnDayClick}
        />

        <div className="cal_footer clearfix">
          <p className="datepicker-legend-start">
            <i>
              <i>&bull;</i>
            </i>
            <strong>At your door</strong>
            by 8pm
          </p>
          <p className="datepicker-legend-end">
            <i>
              <i>&bull;</i>
            </i>
            <strong>Drop in mail</strong>
            by 5pm
          </p>
        </div>
      </Panel>
    </div>
  );
};
